// Datos DEMO deterministas (PRNG con semilla fija) — mismo contrato que el futuro BFF /api/pulse/*
// En fase 1 real, api/client.ts sustituye este módulo por fetch al BFF de Hotelos.

export type Pms = 'noray' | 'avalon' | 'navision' | 'onasystem';

export interface Hotel {
  n: string;
  pms: Pms;
  d: number;      // producción ayer
  occ: number;    // ocupación hoy %
  adr: number;
  bud: number;    // % vs budget del mes
  mes: number;    // producción mes
  spark: number[];
  occ30: number[];
}

const makeRnd = (seed: number) => () => (seed = Math.imul(48271, seed) % 2147483647) / 2147483647;
const rnd = makeRnd(20260821);

const BASE: Array<[string, Pms, number, number, number, number]> = [
  ['Ona Palmira Paradise', 'noray', 41230, 96.4, 132.6, 107.2],
  ['Ona Alanda Club', 'noray', 38940, 93.1, 151.8, 104.8],
  ['Ona Valle Romano', 'noray', 37615, 91.7, 128.4, 103.1],
  ['Ona Hacienda del Álamo', 'noray', 35480, 88.9, 117.2, 101.6],
  ['Ona Garden Lago', 'noray', 34210, 95.2, 109.8, 106.3],
  ['Ona Aucanada', 'noray', 33870, 97.8, 121.5, 108.9],
  ['Ona Marinas de Nerja', 'noray', 33145, 90.4, 114.9, 102.2],
  ['Ona Cala Pi', 'noray', 31980, 94.6, 105.3, 105.4],
  ['Ona Jardines Paraisol', 'noray', 30760, 89.2, 98.7, 99.1],
  ['Ona Don Juan', 'avalon', 29540, 92.8, 96.4, 103.7],
  ['Ona Mar Menor', 'noray', 27315, 81.3, 92.1, 93.8],
  ['Ona Ogisaka Garden', 'noray', 26480, 87.5, 101.2, 100.4],
  ['Ona Marrakech', 'navision', 24130, 78.6, 88.9, 96.2],
  ['Ona Agadir Beach', 'navision', 23870, 82.4, 84.3, 98.5],
  ['Ona Bahía Blanca', 'onasystem', 21340, 85.1, 89.6, 97.4],
  ['Ona Luna Park', 'noray', 17415, 79.8, 86.2, 91.3],
];

export const HOTELS: Hotel[] = BASE.map(([n, pms, d, occ, adr, bud]) => {
  const mes = Math.round(d * 19.4 * (0.92 + rnd() * 0.16));
  const spark = Array.from({ length: 7 }, () => Math.round(d * (0.78 + rnd() * 0.4)));
  spark[6] = d;
  const occ30 = Array.from({ length: 30 }, (_, i) => {
    const wave = Math.sin(i / 4.4) * 6;
    const wk = i % 7 >= 5 ? 5 : 0;
    return Math.min(100, Math.max(58, Math.round(occ - 4 + wave + wk + (rnd() * 10 - 5))));
  });
  return { n, pms, d, occ, adr, bud, mes, spark, occ30 };
});
HOTELS[5].occ30[23] = 102; // overbooking Aucanada día 24 — misma historia en heatmap, story y alerta

export const G = {
  prodDia: 487320,
  prodMes: 9840000,
  budMes: 9500000,
  budPct: 103.6,
  deltaDia: 3.6,
  occ: 88.4,
  adr: 121.4,
  revpar: 107.3,
  inHouse: 6412,
  lleg: 342,
  sal: 298,
  pickup: 1240,
  gop: 2940000,
  rooms: { limpias: 412, sucias: 186, ret: 23, nousar: 9 },
  ageing: [
    { t: '0–30 días', v: 1120000 },
    { t: '31–60', v: 540000 },
    { t: '61–90', v: 310000 },
    { t: '+90', v: 440000 },
  ],
  cobros: 412480,
  dso: 34,
  cierres: { ok: 12, tot: 14, pend: ['Bainbridge', 'Explotadora Sureste'] },
  club: { ytd: 7140000, plan: 76, morosos: 1203, dMor: -38 },
  rrhh: { coste: 3210000, bud: 3280000, ratio: 31.2, plantilla: 1284, dPl: 12, hextra: 2140 },
  asset: { activos: 46, uds: 8770, enlazadas: 7760, contratos: 430, renta: 12400000 },
};

/* 🔴 `dh` y `reg` no son «Dirección General en pequeño». Un director de hotel
   no necesita el ranking de 53 hoteles: necesita SU hotel al detalle —el día
   por punto de venta, su ocupación, su ADR y sus avisos— y saber dónde está en
   el grupo. Un regional necesita comparar LOS SUYOS entre sí. Por eso tienen
   portada propia y no una versión recortada de otra. El alcance (qué hoteles)
   lo sigue mandando Hotelos: aquí solo se decide QUÉ SE ENSEÑA. */
export type RoleId = 'dg' | 'dh' | 'reg' | 'ops' | 'rev' | 'fin' | 'sis' | 'rrhh' | 'ast';

export interface Role {
  id: RoleId;
  ic: string;
  t: string;
  m: string;
  short: string;
  ini: string;
}

export const ROLES: Role[] = [
  { id: 'dg', ic: 'helm', t: 'Dirección General', m: 'Todo el grupo de un vistazo', short: 'Dirección', ini: 'DG' },
  { id: 'reg', ic: 'pin', t: 'Dirección Regional', m: 'Tus hoteles, uno al lado del otro', short: 'Regional', ini: 'DR' },
  { id: 'dh', ic: 'hoteles', t: 'Dirección de Hotel', m: 'Tu hotel al detalle, día a día', short: 'Hotel', ini: 'DH' },
  { id: 'ops', ic: 'bellhop', t: 'Operaciones', m: 'El hotel de hoy: llegadas, pisos, incidencias', short: 'Operaciones', ini: 'OP' },
  { id: 'rev', ic: 'trend', t: 'Ingresos · Revenue', m: 'Pickup, ADR y los próximos 30 días', short: 'Revenue', ini: 'IN' },
  { id: 'fin', ic: 'coin', t: 'Financiero', m: 'Caja, ageing, cierres y club', short: 'Financiero', ini: 'FI' },
  { id: 'sis', ic: 'nodes', t: 'Sistemas', m: 'Salud de los 4 PMS y las sincronizaciones', short: 'Sistemas', ini: 'SI' },
  { id: 'rrhh', ic: 'people', t: 'RRHH', m: 'Coste de personal vs budget y plantilla', short: 'RRHH', ini: 'RH' },
  { id: 'ast', ic: 'key', t: 'Asset Management', m: 'Portafolio, contratos y vencimientos', short: 'Asset', ini: 'AM' },
];

export type Sev = 'crit' | 'wrn' | 'inf' | 'okk';

export interface Alerta {
  ic: string;
  sev: Sev;
  t: string;
  m: string;
  tm: string;
  dep: string;
  day: 'hoy' | 'ayer';
  hotel?: string;
  pms?: string;
  detalle?: Array<[string, string]>;   // filas del acordeón al expandir
  nota?: string;
  /* Los tres de abajo los pone el servidor (avisos.js). En el demo no vienen:
     una alerta sin `id` no se puede marcar leída, y se trata como no leída. */
  codigo?: string;    // OVERBOOKING · PROD_FALTA · SYNC_FALLO
  /* Las claves del aviso en Hotelos (`notificaciones_vistas`). Una tarjeta puede
     cubrir varios hechos —tres días de overbooking son tres claves— y solo está
     leída cuando lo están todas. */
  claves?: string[];
  leido?: boolean;
}

export const ALERTS: Alerta[] = [
  {
    ic: 'bed', sev: 'crit', t: 'Overbooking en Ona Aucanada', m: '24 de agosto · +4 habitaciones sobre capacidad · canal TTOO', tm: '07:41', dep: 'ops rev', day: 'hoy',
    detalle: [['24/08', '102,0%'], ['25/08', '101,3%'], ['Canal', 'TTOO · release incumplido'], ['Capacidad', '278 hab. · vendidas 282']],
    nota: 'Umbral 100,4% — el mismo de la rejilla Ocupación 4 PMS.',
  },
  {
    ic: 'chartdown', sev: 'wrn', t: 'Mar Menor −6,2% vs budget en F&B', m: 'Agosto acumulado · el resto de líneas en verde', tm: '07:30', dep: 'dg fin rev', day: 'hoy',
    detalle: [['F&B real', '184.300 €'], ['F&B budget', '196.500 €'], ['Bar piscina', '−81% del gap'], ['Resto de líneas', 'en verde ✓']],
    nota: 'Fuente: Cuenta de Explotación (mes en curso, sin publicar).',
  },
  {
    ic: 'sync', sev: 'okk', t: 'Avalon recuperado tras reintento', m: 'La sync de las 02:10 falló (conexión) · reintento OK a las 02:14', tm: '02:14', dep: 'sis', day: 'hoy',
    detalle: [['02:10', '✗ fetch failed (conexión)'], ['02:14', '✓ reintento OK · 5.789 filas'], ['Racha', '2ª incidencia esta semana']],
  },
  {
    ic: 'banknote', sev: 'wrn', t: 'Grupo SunTours salta a 61–90 días', m: '84.000 € · pasa al circuito de reclamación', tm: 'ayer 19:02', dep: 'fin', day: 'ayer',
    detalle: [['Deuda total', '84.000 €'], ['Tramo anterior', '31–60 días'], ['Facturas', '3 (jun–jul)'], ['Siguiente paso', 'reclamación formal']],
  },
  {
    ic: 'doc', sev: 'inf', t: 'Contrato Cala Pi vence en 7 meses', m: 'Alquiler · 640 k€/año · preparar renegociación', tm: 'ayer 09:00', dep: 'ast dg', day: 'ayer',
    detalle: [['Tipo', 'Alquiler'], ['Renta', '640 k€/año'], ['Vencimiento', 'marzo 2027'], ['Prórroga', 'a negociar']],
  },
  {
    ic: 'check', sev: 'okk', t: 'Cola de carga Noray despejada', m: '0 pendientes tras el pico de las 23:00 (1.240 tareas)', tm: 'ayer 23:48', dep: 'sis', day: 'ayer',
    detalle: [['Pico', '23:00 · 1.240 tareas'], ['Despeje', '23:48 · 0 pendientes'], ['Duración', '48 min']],
  },
];

export interface Slide {
  colors: [string, string];
  kick: string;
  h: string;
  sub: string;
  foot: string;
  waves?: boolean;
  tortugona?: boolean;   // el hotel del día luce la Tortugona de oro
}

export const SLIDES: Slide[] = [
  { colors: ['#0E2B3D', '#128F86'], kick: 'Ona Pulse · hoy', h: 'Buenos días', sub: 'Esto pasó ayer en tu grupo', foot: '6 momentos · 10 segundos', waves: true },
  { colors: ['#07222F', '#0B4A4C'], kick: 'Producción de ayer', h: '487.320 €', sub: '+3,6% sobre budget', foot: 'Los 16 hoteles reportaron antes de las 06:15 · 4 PMS' },
  { colors: ['#2B2410', '#7A5A16'], kick: 'El hotel del día', h: 'Palmira Paradise', sub: '41.230 € · su mejor día del mes', foot: 'Ocupación 96,4% · ADR 132,60 € · felicita al equipo 👏' },
  { colors: ['#07222F', '#0E3A5C'], kick: 'Esta noche', h: '6.412 huéspedes', sub: '88,4% de ocupación · 6 hoteles por encima del 95%', foot: 'Llegadas hoy: 342 · pico de check-in 12–14 h' },
  { colors: ['#331A16', '#7A2E2A'], kick: 'Atención', h: 'Overbooking en Aucanada', sub: '24 de agosto · +4 habitaciones', foot: 'Detectado por el chequeo diario · Revenue ya avisado' },
  { colors: ['#0E2B3D', '#0F6E6A'], kick: 'Agosto va así', h: '103,6% del budget', sub: 'GOP proyectado: 2,94 M€', foot: 'Que tengas buen día. Mañana a las 8:00, más olas', waves: true },
];

export const IA_SUGG = [
  '¿Qué hoteles van por debajo del budget?',
  '¿Cómo cerró ayer Don Juan?',
  '¿Cómo está el ageing de clientes?',
];

export interface IaAnswer {
  k: string[];
  txt: string;
  rows: Array<[string, string, 'good' | 'bad' | 'flat']>;
  foot: string;
}

export const IA_ANSWERS: IaAnswer[] = [
  {
    k: ['budget', 'debajo', 'desv'],
    txt: '3 hoteles van por debajo del budget de agosto:',
    rows: [['Ona Mar Menor', '−6,2%', 'bad'], ['Ona Luna Park', '−8,7%', 'bad'], ['Ona Marrakech', '−3,8%', 'bad']],
    foot: 'El 81% del gap de Mar Menor está en F&B (bar piscina). ¿Quieres el desglose por línea?',
  },
  {
    k: ['don juan', 'ayer', 'cerró'],
    txt: 'Ona Don Juan cerró ayer así:',
    rows: [['Producción', '29.540 €', 'good'], ['Ocupación', '92,8%', 'good'], ['ADR', '96,40 €', 'flat']],
    foot: 'Va +3,7% contra budget en el mes. Fuente: Avalon, sync de las 02:14.',
  },
  {
    k: ['ageing', 'deuda', 'cobr'],
    txt: 'El ageing total es de 2,41 M€:',
    rows: [['0–30 días', '1,12 M€', 'good'], ['31–60', '540 k€', 'flat'], ['+60', '750 k€', 'bad']],
    foot: 'Lo urgente: SunTours (84 k€) acaba de saltar a 61–90. ¿Aviso al equipo de cobros?',
  },
  {
    k: ['gop', 'margen'],
    txt: 'GOP proyectado de agosto: 2,94 M€ (+6,8% vs budget).',
    rows: [['TNR', '9,12 M€', 'good'], ['Coste operativo', '−4,89 M€', 'flat'], ['Personal', '−1,29 M€', 'good']],
    foot: 'El forecast 12M del grupo va al 103% del plan.',
  },
];
